<template>
  <div class="financial-analysis">
    <div class="financial-analysis__cards">
      <KpiCard
        v-for="card in cards"
        :key="card.title"
        :title="card.title"
        :value="card.value"
        :complement="card.complement"
        :complement-variant="card.complementVariant"
        :description="card.description"
        :info="card.info"
      />
    </div>
    <div class="financial-analysis__charts">
      <HorizontalBarChart
        title="Composição por Especialidade"
        :data="specialties"
        :loading="loading"
        unit=""
        :colors="specialtyColors"
      />
      <LineChart
        title="Evolução do Faturamento (R$)"
        :categories="months"
        :values="values"
        :loading="loading"
        series-name="Faturamento"
        :show-target="false"
        :height="236"
        :info-tooltip="revenueInfoTooltip"
        :y-axis-formatter="formatRevenueAxis"
        :tooltip-formatter="formatRevenueTooltip"
      />
    </div>
    <div class="financial-analysis__table-section">
      <Top5List
        :loading="loading"
        :data="topExams"
        :columns="topExamsColumns"
        sort-key="totalValue"
        title="Top 5 Exames no período"
      >
        <template #footer>
          <button
            type="button"
            class="financial-analysis__view-all"
            @click="onViewAll"
          >
            Ver todos
            <md-icon md-src="/static/images/icon-download-arrow.svg" />
          </button>
        </template>
      </Top5List>
    </div>
    <ReminderCard
      titulo="Lista de preços negociados"
      descricao="Para consultar sua lista completa de exames com preços negociados exclusivamente com o Álvaro Apoio, clique no botão abaixo."
      texto-botao="Acessar lista de preços"
      @click-botao="onAccessPriceList"
    />
  </div>
</template>

<script>
import KpiCard from '../charts/KpiCard.vue'
import HorizontalBarChart from '../charts/HorizontalBarChart.vue'
import LineChart from '../charts/LineChart.vue'
import ReminderCard from '../../common/ReminderCard.vue'
import Top5List from '../charts/Top5List.vue'
import biFinancialAnalysis from '../../../api/biFinancialAnalysis'

export default {
  name: 'FinancialAnalysis',
  components: {
    KpiCard,
    HorizontalBarChart,
    LineChart,
    ReminderCard,
    Top5List,
  },
  props: {
    period: { type: String, default: null },
  },
  data() {
    return {
      revenueInfoTooltip: {
        label: 'Evolução do Faturamento',
        text: 'Faturamento mês a mês nos últimos 12 meses. Os períodos indicados consideram o seu ciclo de faturamento.',
      },
      loading: false,
      cards: [
        {
          title: 'Total no período',
          value: '--',
          info: 'Soma estimada do faturamento de todos os exames liberados no período. Não inclui exames com recoleta, pendência em aberto ou amostras extraviadas, pois esses não geram faturamento.',
        },
        {
          title: 'Exames realizados',
          value: '--',
          complement: '',
          complementVariant: '',
          description: '',
          info: 'Quantidade de exames efetivamente liberados e cobrados no período. Não inclui exames com recoleta, pendência em aberto ou amostras extraviadas, pois esses não geram faturamento.',
        },
        {
          title: 'Ticket Médio',
          value: '--',
          info: 'Valor médio cobrado por exame no período. É calculado dividindo o “Total no período” pelo número de “Exames realizados”.',
        },
      ],
      months: [],
      values: [],
      specialties: [],
      specialtyColors: ['#7095DD', '#F7A74E', '#C772A9', '#7FBFCC', '#B6B6B6'],
      topExams: [],
      topExamsColumns: [
        { key: 'name', title: 'Exame', indexPrefix: true, flex: 2.5 },
        { key: 'specialty', title: 'Especialidade', flex: 1.2 },
        { key: 'averageTicket', title: 'Ticket médio', align: 'right', flex: 1 },
        { key: 'quantity', title: 'Qtd.', align: 'right', flex: 0.6 },
        { key: 'total', title: 'Total', align: 'right', flex: 1 },
        { key: 'percentage', title: 'Percentual', align: 'right', flex: 0.8 },
      ],
    }
  },
  computed: {
    currentPeriod() {
      return new Date().toISOString().slice(0, 7)
    },
  },
  watch: {
    period(value) {
      if (value) this.fetchFinancialAnalysis(value)
    },
  },
  created() {
    this.fetchFinancialAnalysis(this.period || this.currentPeriod)
  },
  methods: {
    async fetchFinancialAnalysis(period) {
      this.loading = true
      this.resetData()
      try {
        const { data } = await biFinancialAnalysis.getFinancialAnalysis(period)
        this.mapData(data)
      } catch (error) {
        console.error('[BI] Erro ao buscar análise financeira:', error)
      } finally {
        this.loading = false
      }
    },
    resetData() {
      this.cards = this.cards.map(card => ({
        ...card,
        value: '--',
        complement: '',
        complementVariant: '',
        description: '',
      }))
      this.months = []
      this.values = []
      this.specialties = []
      this.topExams = []
    },
    mapData(data) {
      if (!data) return

      const summary = data.summary || {}
      const examsChange = summary.examesRealizados?.changeVsPreviousMonth
      this.cards = [
        {
          ...this.cards[0],
          value: this.formatCurrency(summary.totalFaturado?.value),
        },
        {
          ...this.cards[1],
          value: (summary.examesRealizados?.value ?? 0).toLocaleString('pt-BR'),
          complement:
            typeof examsChange === 'number'
              ? `${examsChange >= 0 ? '+' : ''}${(examsChange * 100).toFixed(2).replace('.', ',')}%`
              : '',
          complementVariant: examsChange >= 0 ? 'green' : 'red',
          description: typeof examsChange === 'number' ? 'vs. mês anterior' : '',
        },
        {
          ...this.cards[2],
          value: this.formatCurrency(summary.ticketMedio?.value),
        },
      ]

      this.specialties = (data.charts?.composicaoPorEspecialidade?.items ?? []).map(
        item => ({
          name: item.especialidadeDescricao,
          value: item.total,
          label: this.formatCurrency(item.total),
          percent: item.percentage ?? 0,
          quantity: item.quantidade,
          detail: `(${(item.quantidade ?? 0).toLocaleString('pt-BR')})`,
        })
      )

      const revenue = data.charts?.evolucaoFaturamento?.items ?? []
      this.months = revenue.map(item => this.formatMonth(item.period))
      this.values = revenue.map(item => item.total)

      this.topExams = (data.topExames?.items ?? []).map(item => ({
        name: item.exameDescricao,
        specialty: item.especialidadeDescricao,
        averageTicket: this.formatCurrency(item.ticketMedio),
        quantity: (item.quantidade ?? 0).toLocaleString('pt-BR'),
        total: this.formatCurrency(item.total),
        percentage: `${(item.percentage ?? 0).toFixed(2).replace('.', ',')}%`,
        totalValue: item.total,
      }))

      if (data.dataStatus?.updatedAt) {
        this.$emit('updated-at', data.dataStatus.updatedAt)
      }
    },
    formatCurrency(value) {
      if (typeof value !== 'number') return '--'
      return value.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    },
    formatMonth(period) {
      const [year, month] = period.split('-')
      const monthLabels = [
        'Jan',
        'Fev',
        'Mar',
        'Abr',
        'Mai',
        'Jun',
        'Jul',
        'Ago',
        'Set',
        'Out',
        'Nov',
        'Dez',
      ]
      return `${monthLabels[Number(month) - 1]}\n${year.slice(2)}`
    },
    formatRevenueAxis(value) {
      return `${Math.round(value / 1000)} K`
    },
    formatRevenueTooltip(params) {
      const label = params[0].axisValue.replace('\n', '/')
      const value = Number.isFinite(params[0].value)
        ? params[0].value.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : '--'
      return `<div style="font-family:Roboto,sans-serif;font-weight:700;font-size:14px;color:#464646">${label}</div><div style="font-family:Roboto,sans-serif;font-size:14px;color:#464646">R$ ${value}</div>`
    },
    onViewAll() {
      this.$emit('view-all')
    },
    onAccessPriceList() {
      this.$router.push({ name: 'Faturamento', query: { tab: 'precos' } })
    },
  },
}
</script>

<style scoped lang="scss">
.financial-analysis {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.financial-analysis__cards {
  display: flex;
  gap: 16px;
}
.financial-analysis__charts {
  display: flex;
  gap: 16px;
}
.financial-analysis__table-section h3 {
  margin: 0;
}
.financial-analysis__table-section {
  padding: 16px;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
}
.financial-analysis__view-all {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #7f1a54;
  font-family: 'Exo 2', sans-serif;
  font-weight: 500;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;

  .md-icon {
    width: 24px;
    height: 24px;
    min-width: 24px;
    min-height: 24px;
    margin: 0;
  }

  // garante a cor do ícone independente da ordem de carregamento do CSS entre ambientes
  ::v-deep svg .color {
    fill: #7f1a54 !important;
  }
}
</style>
